/**
 * insertSelectPairing — pure helpers for the INSERT…SELECT column-pairing
 * card (DML-10 / DML-11). Pairs each target column (an INSERT's explicit
 * column list) against the positionally-corresponding source SELECT
 * expression, so a first-time reader can verify e.g. `now()` really lands in
 * `payment_date` without counting pills. Read-only / display-only: nothing
 * here emits or edits SQL.
 *
 * Reuses the existing SELECT-renderer extractors rather than writing a new
 * tokenizer — `extractSelectOutline` locates the select-list span,
 * `splitTopLevelCommas` chops it into top-level items, `parseSelectItem`
 * splits each item into `{expression, alias}` (VQE-RO9 — structural, no ad
 * hoc regex parsing of SQL).
 */

import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import { extractSelectOutline } from '../select/extractSelectOutline';
import { parseSelectItem } from '../select/parseSelectItem';
import { splitTopLevelCommas } from '../select/splitTopLevel';

export interface SourceSelectItem {
  readonly expression: string;
  readonly alias: string | null;
  /** True when this item is a `*` / `t.*` star — its contribution to the
   *  column count can't be known statically. */
  readonly isStar: boolean;
}

function fullRange(source: string): SqlRange {
  return {
    startOffset: 0,
    endOffset: source.length,
    startLine: 0,
    startColumn: 0,
    endLine: 0,
    endColumn: source.length,
  };
}

function isStarExpression(expression: string): boolean {
  const trimmed = expression.trim();
  return trimmed === '*' || trimmed.endsWith('.*');
}

/**
 * Extracts the top-level SELECT items of a source query's text (already
 * sliced to just the SELECT statement — e.g. `InsertRenderer`'s
 * `selectSourceStart`/`selectSourceEnd` span). Returns `[]` when the text
 * doesn't parse as a SELECT with a select-list (defensive — callers treat an
 * empty result as `'not-applicable'`, never worse than rendering nothing).
 */
export function extractSourceSelectItems(
  sourceText: string,
  options: ParseOptions,
): readonly SourceSelectItem[] {
  const outline = extractSelectOutline(sourceText, fullRange(sourceText), options);
  if (!outline.selectList) return [];
  const { start, end } = outline.selectList;
  const rawItems = splitTopLevelCommas(sourceText, start, end, options);
  return rawItems.map((item) => {
    const parsed = parseSelectItem(sourceText, item.start, item.end, options);
    return {
      expression: parsed.expression,
      alias: parsed.alias,
      isStar: isStarExpression(parsed.expression),
    };
  });
}

/** Pairing outcome — string union, no enum (project convention). */
export type PairingStatus =
  | 'ok'
  | 'count-mismatch'
  | 'star-source'
  | 'no-target-list'
  | 'not-applicable';

export interface InsertSelectPair {
  readonly index: number;
  readonly target: string | null;
  readonly source: { readonly expression: string; readonly alias: string | null } | null;
}

export interface InsertSelectPairingResult {
  readonly pairs: readonly InsertSelectPair[];
  readonly status: PairingStatus;
  /** Target column count — 0 when there's no explicit column list. */
  readonly targetCount: number;
  /** Source select-item count. */
  readonly sourceCount: number;
}

/**
 * Pairs target columns (an INSERT's explicit column list, or `null` when
 * omitted) against source SELECT items positionally.
 *
 * Rule order (first match wins):
 *  1. No source items at all (unparseable source) → `'not-applicable'`.
 *  2. Any source item is a `*` star → `'star-source'` (counts unknowable
 *     statically; pair rows aren't meaningful — a note, not a false alarm).
 *  3. No explicit target column list → `'no-target-list'`, pairs still
 *     zip positionally (`target: null`) so the source items are at least
 *     numbered.
 *  4. Equal lengths → `'ok'`, pairs zip 1:1.
 *  5. Unequal lengths → `'count-mismatch'`, pairs zip to the LONGER side
 *     (unmatched tail's `target`/`source` is `null`).
 */
export function pairInsertColumns(
  targetColumns: readonly string[] | null,
  sourceItems: readonly SourceSelectItem[],
): InsertSelectPairingResult {
  const sourceCount = sourceItems.length;

  if (sourceCount === 0) {
    return { pairs: [], status: 'not-applicable', targetCount: targetColumns?.length ?? 0, sourceCount };
  }

  if (sourceItems.some((item) => item.isStar)) {
    return { pairs: [], status: 'star-source', targetCount: targetColumns?.length ?? 0, sourceCount };
  }

  if (targetColumns === null) {
    const pairs = sourceItems.map((item, index) => ({
      index,
      target: null,
      source: { expression: item.expression, alias: item.alias },
    }));
    return { pairs, status: 'no-target-list', targetCount: 0, sourceCount };
  }

  const targetCount = targetColumns.length;
  const length = Math.max(targetCount, sourceCount);
  const pairs: InsertSelectPair[] = [];
  for (let index = 0; index < length; index++) {
    pairs.push({
      index,
      target: index < targetCount ? targetColumns[index] : null,
      source:
        index < sourceCount
          ? { expression: sourceItems[index].expression, alias: sourceItems[index].alias }
          : null,
    });
  }

  return {
    pairs,
    status: targetCount === sourceCount ? 'ok' : 'count-mismatch',
    targetCount,
    sourceCount,
  };
}
